/**
 * PmtilesVectorLayer.tsx — signed-URL PMTiles vector choropleth for the aggregate tiers (T-102).
 *
 * The zoom-out aggregate tiers (state / county) can be served as single-file PMTiles pyramids from
 * a private CloudFront distribution instead of the bbox-GeoJSON fetch: given a time-limited signed
 * URL (GET /api/tile-auth), this adds a MapLibre `vector` source over the `pmtiles://` protocol and
 * renders the SAME choropleth on `lvi` as StateLayer / CountyLayer, so the proprietary score reads
 * identically whichever transport is active. When no signed URL is available (`url` is null),
 * MapShell mounts the bbox layer instead and this renders nothing.
 *
 * The `pmtiles` reader resolves the pyramid over HTTP Range requests against the one signed URL;
 * the signature covers the resource path (not the Range header), so every range fetch validates.
 * On a refresh the `url` prop changes → the source is torn down and re-added with the fresh URL.
 *
 * Lifecycle mirrors StateLayer/CountyLayer: source + fill + outline added together; a separate
 * effect toggles visibility via setLayoutProperty (LOD tier switch) without a source teardown.
 */
import { useEffect, useRef } from 'react'
import maplibregl from 'maplibre-gl'
import type { MapLayerMouseEvent, ErrorEvent } from 'maplibre-gl'
import { useMap } from './MapContext'
import { isMapAlive } from '../../lib/mapLiveness'
import { registerPmtilesProtocol } from '../../lib/pmtilesProtocol'

export interface PmtilesVectorLayerProps {
  /** Full signed CloudFront pmtiles URL (…/xxx.pmtiles?Policy=…). null → renders nothing. */
  url: string | null
  /** Vector-tile source-layer inside the pyramid (tippecanoe -l): 'state' | 'county'. */
  sourceLayer: string
  /** Stable id prefix for the source + layers, e.g. 'state-pm'. */
  idPrefix: string
  /** Feature property used as the hover label (e.g. 'state_abbrev' | 'county_name'). */
  labelProp: string
  /**
   * T-380: optional transform applied to the raw label value before it is rendered. The state
   * pyramid carries `state_abbrev` ("PA"), which a non-US reader cannot decode, so MapShell
   * passes lib/stateNames.stateLabel for that tier. Omitted for tiers whose property is already
   * human-readable (county_name), where the raw value IS the label.
   */
  formatLabel?: (raw: string) => string
  /** Show/hide the layer (driven by the active LOD tier). Default on. */
  visible?: boolean
  /**
   * T-116: called when MapLibre reports an error for THIS pmtiles source — a failed signed range
   * fetch (403 / expired or placeholder-key signature, network error). MapShell uses it to fall
   * back to the bbox-GeoJSON tier instead of leaving the aggregate tier blank. The signed URL can
   * be valid at /api/tile-auth (200) yet still 403 at CloudFront if the key doesn't match, which is
   * exactly the failure that left the tiers blank for ~1h48m on 2026-07-06 (T-102 degraded window).
   */
  onSourceError?: () => void
}

// Same colour stops as StateLayer / CountyLayer / TractLayer's choropleth — the score meaning is
// identical across every tier and transport, so the ramp must not drift.
const CHOROPLETH_FILL_COLOR = [
  'interpolate',
  ['linear'],
  ['get', 'lvi'],
  0,
  '#ef4444',
  35,
  '#f97316',
  45,
  '#f59e0b',
  60,
  '#4ade80',
  70,
  '#15803d',
] as unknown as maplibregl.ExpressionSpecification

export function PmtilesVectorLayer({
  url,
  sourceLayer,
  idPrefix,
  labelProp,
  formatLabel,
  visible = true,
  onSourceError,
}: PmtilesVectorLayerProps): null {
  const map = useMap()
  const sourceId = `${idPrefix}-src`
  const fillId = `${idPrefix}-fill`
  const outlineId = `${idPrefix}-outline`

  // Keep the latest onSourceError in a ref so the add-source effect can call it without listing it
  // as a dependency — an inline callback from MapShell changes identity every render, and we must
  // not tear down / re-add the source (and re-fire tile fetches) on every parent re-render.
  const onSourceErrorRef = useRef(onSourceError)
  useEffect(() => {
    onSourceErrorRef.current = onSourceError
  }, [onSourceError])

  // Same reason for the T-380 label formatter: the hover handler is installed once with the
  // source, so read the latest formatter through a ref rather than making an inline arrow from
  // the parent re-add the source (and re-fetch every tile) on each render.
  const formatLabelRef = useRef(formatLabel)
  useEffect(() => {
    formatLabelRef.current = formatLabel
  }, [formatLabel])

  // Add source + fill + outline + hover tooltip. Re-runs (teardown → re-add) when the signed URL
  // refreshes. No-op while `url` is null (the bbox fallback layer is mounted in that case).
  useEffect(() => {
    if (!map || !url) return
    registerPmtilesProtocol()

    map.addSource(sourceId, { type: 'vector', url: `pmtiles://${url}` })

    map.addLayer({
      id: fillId,
      type: 'fill',
      source: sourceId,
      'source-layer': sourceLayer,
      paint: { 'fill-color': CHOROPLETH_FILL_COLOR, 'fill-opacity': 0.6 },
    })
    map.addLayer({
      id: outlineId,
      type: 'line',
      source: sourceId,
      'source-layer': sourceLayer,
      paint: { 'line-color': '#ffffff', 'line-width': 0.8, 'line-opacity': 0.6 },
    })

    const hoverPopup = new maplibregl.Popup({ closeButton: false, closeOnClick: false, offset: 10 })

    // T-392: `mousemove`, NOT `mouseenter`. MapLibre's enter/leave pair is LAYER-scoped, so
    // dragging the cursor from one state into the next never leaves this fill layer and neither
    // event fires — the popup keeps showing whichever feature the pointer first entered, until
    // it exits every polygon in the layer. Measured on the real pyramid: one 27-step sweep
    // across the PA/NJ border produced exactly ONE mouseenter, and the tooltip still read "PA"
    // at every step inside NJ.
    let shownKey: string | null = null
    let added = false

    const handleMove = (e: MapLayerMouseEvent) => {
      map.getCanvas().style.cursor = 'pointer'
      const f = e.features?.[0]
      if (!f?.properties) return
      const p = f.properties as Record<string, unknown>
      const score = Math.round(Number(p['lvi'] ?? 0))
      const raw = String(p[labelProp] ?? '')
      const fmt = formatLabelRef.current
      const name = fmt ? fmt(raw) : raw

      // Position tracks the cursor on every move; the HTML is rebuilt only when the feature
      // under it actually changes. Re-parsing identical markup ~60×/s is pure churn (the whole
      // dispatch measures ~0.8 ms median, so this is about DOM work, not about the fix).
      hoverPopup.setLngLat(e.lngLat)
      const key = `${name}|${score}`
      if (key !== shownKey) {
        shownKey = key
        hoverPopup.setHTML(
          `<div style="font-family:Inter,sans-serif;font-size:12px;padding:2px 0">
            <span style="font-weight:700;font-size:16px;color:var(--foreground)">${score}</span>
            <span style="color:var(--muted-foreground);margin-left:4px">Opportunity Score</span><br/>
            <span style="color:var(--muted-foreground);font-size:11px">${name}</span>
          </div>`,
        )
      }
      if (!added) {
        hoverPopup.addTo(map)
        added = true
      }
    }
    const handleLeave = () => {
      map.getCanvas().style.cursor = ''
      shownKey = null
      added = false
      hoverPopup.remove()
    }

    // T-116: surface a failed signed range fetch. MapLibre fires a map-level `error` event scoped
    // to the failing source (e.sourceId); when it's ours, tell MapShell to fall back to the bbox
    // tier. Matching on sourceId keeps an unrelated source's error (e.g. the basemap) from tripping
    // this tier's fallback.
    const handleSourceError = (e: ErrorEvent & { sourceId?: string }) => {
      if (e.sourceId === sourceId) onSourceErrorRef.current?.()
    }

    map.on('mousemove', fillId, handleMove)
    map.on('mouseleave', fillId, handleLeave)
    map.on('error', handleSourceError)

    return () => {
      hoverPopup.remove()
      map.off('mousemove', fillId, handleMove)
      map.off('mouseleave', fillId, handleLeave)
      map.off('error', handleSourceError)
      // BEAD-0037: on sign-out MapView's map.remove() runs first, dropping the style; getLayer/
      // removeLayer would then throw and blank the app. Skip teardown once the map is gone.
      if (isMapAlive(map)) {
        if (map.getLayer(outlineId)) map.removeLayer(outlineId)
        if (map.getLayer(fillId)) map.removeLayer(fillId)
        if (map.getSource(sourceId)) map.removeSource(sourceId)
      }
    }
  }, [map, url, sourceLayer, idPrefix, labelProp, fillId, outlineId, sourceId])

  // Visibility toggle (LOD tier switch). Keyed on `url` too so it re-applies after a refresh
  // re-adds the source.
  useEffect(() => {
    if (!map) return
    const vis = visible ? 'visible' : 'none'
    if (map.getLayer(fillId)) map.setLayoutProperty(fillId, 'visibility', vis)
    if (map.getLayer(outlineId)) map.setLayoutProperty(outlineId, 'visibility', vis)
  }, [map, url, visible, fillId, outlineId])

  return null
}
